import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { MailX } from "lucide-react";
import * as React from "react";

import { AppShell } from "@/components/app/app-shell";
import { Input } from "@/components/ui/input";
import { EMAIL_CATEGORIES } from "@/lib/email-preferences";
import {
  adminListPreferenceEvents,
  type PreferenceEventRow,
} from "@/lib/preference-events.functions";

export const Route = createFileRoute("/_authenticated/t1dwpanel/opt-outs")({
  head: () => ({
    meta: [
      { title: "Email opt-out log | Warrior Volunteer" },
      {
        name: "description",
        content: "Audit log of every unsubscribe and email preference change.",
      },
      { property: "og:title", content: "Email opt-out log | Warrior Volunteer" },
      {
        property: "og:description",
        content: "Audit log of every unsubscribe and email preference change.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: OptOutLogPage,
});

function when(value: string) {
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const ACTION_LABELS: Record<string, string> = {
  unsubscribe_all: "Unsubscribed from all",
  resubscribe: "Re-subscribed",
  preferences_update: "Updated preferences",
  campaign_unsubscribe: "One-click unsubscribe",
};

function describeChange(row: PreferenceEventRow) {
  const off = Object.entries(row.categories ?? {})
    .filter(([, on]) => !on)
    .map(([key]) => EMAIL_CATEGORIES.find((c) => c.key === key)?.label ?? key);
  if (row.unsubscribed_all) return "All email off";
  if (row.action === "campaign_unsubscribe") return "Recruiting invitations off";
  if (off.length === 0) return "All categories on";
  return `Off: ${off.join(", ")}`;
}

function OptOutLogPage() {
  const [emailFilter, setEmailFilter] = React.useState("");
  const [debounced, setDebounced] = React.useState("");
  const list = useServerFn(adminListPreferenceEvents);

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(emailFilter.trim()), 300);
    return () => clearTimeout(t);
  }, [emailFilter]);

  const { data, isLoading } = useQuery({
    queryKey: ["preference-events", debounced],
    queryFn: () => list({ data: { email: debounced || undefined } }),
  });

  const rows = data ?? [];

  return (
    <AppShell
      title="Email opt-out log"
      eyebrow="Admin"
      actions={
        <Input
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
          placeholder="Filter by email"
          className="w-56"
        />
      }
    >
      <p className="text-sm text-muted-foreground">
        Every time a recipient unsubscribes or changes their email preferences, it is
        recorded here with the campaign message it came from and the exact time.
      </p>

      <div className="mt-4 overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2 font-medium">When</th>
              <th className="px-3 py-2 font-medium">Recipient</th>
              <th className="px-3 py-2 font-medium">Action</th>
              <th className="px-3 py-2 font-medium">Resulting preferences</th>
              <th className="px-3 py-2 font-medium">Campaign message</th>
              <th className="px-3 py-2 font-medium">Source</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center">
                  <MailX className="mx-auto mb-2 size-6 text-muted-foreground" />
                  <p className="font-medium">No preference changes recorded yet</p>
                  <p className="text-muted-foreground">
                    Unsubscribes and preference updates will appear here as they happen.
                  </p>
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-border last:border-0">
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {when(row.created_at)}
                </td>
                <td className="px-3 py-2 font-medium">{row.email}</td>
                <td className="px-3 py-2">
                  {ACTION_LABELS[row.action] ?? row.action}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{describeChange(row)}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {row.campaign_send_id ? (
                    <span title={row.provider_message_id ?? undefined}>
                      Step {row.campaign_step ?? "?"}
                      {row.provider_message_id ? " · tracked" : ""}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{row.source ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
